import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  getTravellerGlobalSearchQueryKey,
  usePurgeCard,
} from '@/api/trafikportalen';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import Button from '@/components/core/Button';
import { WarningDialog } from '@/components/WarningDialog';
import { useStudentsDisabled } from '@/contexts/ConfigContext';
import { getApiErrorMessage } from '@/helpers/apiError';
import { cn } from '@/helpers/classnames';
import {
  getSearchCardPurgeOptions,
  type SearchCardPurgeOption,
  type TravellerSearchCardKind,
} from '@/helpers/travellerSearch';
import { toast } from '@/hooks/useToast';
import {
  IconGraduationCap,
  IconSmartcard,
  IconTrash,
  IconWallet,
} from '@/icons';
import type { TravellerWithCardsDTO } from '@/api/trafikportalen';

interface PurgeCardPanelProps {
  results: TravellerWithCardsDTO[];
  query: string;
}

const CARD_KIND_ICON: Record<TravellerSearchCardKind, typeof IconSmartcard> = {
  travelcard: IconSmartcard,
  walletcard: IconWallet,
  schoolcard: IconGraduationCap,
};

const cardKindLabel = (
  kind: TravellerSearchCardKind,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
) => {
  switch (kind) {
    case 'travelcard':
      return formatMessage({ defaultMessage: 'Resekort' });
    case 'walletcard':
      return formatMessage({ defaultMessage: 'Tjänsteresa på kort' });
    case 'schoolcard':
      return formatMessage({ defaultMessage: 'Skolkort' });
  }
};

const purgeCardErrorMessage = (
  error: unknown,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
): string => {
  if (isAxiosError(error)) {
    if (error.response?.status === 404) {
      return formatMessage({
        defaultMessage: 'Kortet är inte tilldelat.',
      });
    }
    if (error.response?.status === 409) {
      return formatMessage({
        defaultMessage:
          'Kortet gick inte att rensa. Kontrollera så det inte har några icke-nyttjade biljetter på sig.',
      });
    }
  }

  return getApiErrorMessage(
    error,
    formatMessage({
      defaultMessage: 'Ett fel uppstod när kortet skulle rensas.',
    }),
  );
};

export const PurgeCardPanel = ({ results, query }: PurgeCardPanelProps) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const purgeCard = usePurgeCard();
  const studentsDisabled = useStudentsDisabled();
  const canPurge = checkUserPermissions({ permission: 'tenant:editor' });

  const [selectedSerial, setSelectedSerial] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const cards = useMemo(() => {
    const options = getSearchCardPurgeOptions(results, query);
    return studentsDisabled
      ? options.filter((card) => !card.schoolOnly)
      : options;
  }, [results, query, studentsDisabled]);

  useEffect(() => {
    const selectable = cards.filter((card) => card.purgeable);
    setSelectedSerial((current) => {
      if (current && selectable.some((card) => card.serial === current)) {
        return current;
      }
      const [onlyCard] = selectable;
      return selectable.length === 1 && onlyCard ? onlyCard.serial : null;
    });
  }, [cards]);

  const selectedCard = cards.find((card) => card.serial === selectedSerial);
  const hasSchoolCard = cards.some((card) => card.kind === 'schoolcard');
  const hasWalletCard = cards.some((card) => card.kind === 'walletcard');

  const handleOpenConfirm = useCallback(() => {
    if (!selectedSerial) return;
    setConfirmOpen(true);
  }, [selectedSerial]);

  const handleCancel = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!selectedSerial) return;

    const displaySerial = selectedCard?.displaySerial ?? selectedSerial;

    try {
      await purgeCard.mutateAsync({ cardSerial: selectedSerial });
      await queryClient.invalidateQueries({
        queryKey: getTravellerGlobalSearchQueryKey(),
      });
      setConfirmOpen(false);
      toast.success(
        {
          title: formatMessage(
            { defaultMessage: 'Kortet {serial} har rensats' },
            { serial: displaySerial },
          ),
        },
        { autoClose: 3000 },
      );
    } catch (error) {
      toast.error(
        { title: purgeCardErrorMessage(error, formatMessage) },
        { autoClose: 3000 },
      );
    }
  }, [selectedSerial, selectedCard, purgeCard, queryClient, formatMessage]);

  if (!canPurge || cards.length === 0) {
    return null;
  }

  return (
    <section
      className="flex flex-col gap-3 rounded-md border border-background-400 p-4"
      aria-labelledby="purge-card-heading"
    >
      <h2 id="purge-card-heading">
        <FormattedMessage defaultMessage="Rensa kort" />
      </h2>
      <p id="purge-card-description" className="text-text-700">
        <FormattedMessage defaultMessage="Välj ett resekort från sökningen. Rensning tar bort kortets bärare och släpper serienumret så det kan användas igen. Bara resekort utan icke-nyttjade biljetter på sig kan rensas." />
      </p>
      {hasSchoolCard && (
        <p id="purge-school-card-note" className="text-text-700">
          <FormattedMessage defaultMessage="Skolkort måste bytas först, sen kan det rensas" />
        </p>
      )}
      {hasWalletCard && (
        <p id="purge-wallet-card-note" className="text-text-700">
          <FormattedMessage defaultMessage="Tjänsteresa på kort måste tas bort från resenärsvyn" />
        </p>
      )}
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 font-medium">
          <FormattedMessage defaultMessage="Välj kort" />
        </legend>
        {cards.map((card) => (
          <PurgeCardOption
            key={card.serial}
            card={card}
            selected={selectedSerial === card.serial}
            label={cardKindLabel(card.kind, formatMessage)}
            describedBy={
              card.kind === 'schoolcard'
                ? 'purge-school-card-note'
                : card.kind === 'walletcard'
                  ? 'purge-wallet-card-note'
                  : undefined
            }
            onSelect={setSelectedSerial}
          />
        ))}
      </fieldset>
      <div className="flex justify-end">
        <Button
          variant="outline"
          color="red"
          startIcon={<IconTrash aria-hidden="true" />}
          disabled={!selectedSerial || purgeCard.isPending}
          aria-describedby="purge-card-description"
          onClick={handleOpenConfirm}
        >
          <FormattedMessage defaultMessage="Rensa kort" />
        </Button>
      </div>
      <WarningDialog
        open={confirmOpen}
        title={formatMessage({ defaultMessage: 'Rensa kort' })}
        content={
          <FormattedMessage
            defaultMessage="Är du säker på att du vill rensa kortet {serial}? Kortet rensas helt från Bobcat och AllTix. Kortet går inte att rensa om det finns icke-nyttjade biljetter på det"
            values={{
              serial: selectedCard?.displaySerial ?? selectedSerial,
            }}
          />
        }
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        buttonText={formatMessage({ defaultMessage: 'Rensa kort' })}
        buttonIcon={<IconTrash aria-hidden="true" />}
        loading={purgeCard.isPending}
      />
    </section>
  );
};

interface PurgeCardOptionProps {
  card: SearchCardPurgeOption;
  selected: boolean;
  label: string;
  describedBy?: string;
  onSelect: (serial: string) => void;
}

const PurgeCardOption = ({
  card,
  selected,
  label,
  describedBy,
  onSelect,
}: PurgeCardOptionProps) => {
  const Icon = CARD_KIND_ICON[card.kind];

  return (
    <label
      className={cn(
        'flex items-center gap-3 rounded-md border border-background-400 px-3 py-2',
        card.purgeable && 'cursor-pointer hover:bg-background-200',
        !card.purgeable && 'cursor-not-allowed text-text-600',
        selected && 'ring-2 ring-inset ring-primary-500',
      )}
    >
      <input
        type="radio"
        name="purge-card"
        value={card.serial}
        checked={selected}
        disabled={!card.purgeable}
        aria-describedby={describedBy}
        onChange={() => onSelect(card.serial)}
        className="size-4 shrink-0 accent-primary-600"
      />
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      <span className="font-medium">{card.displaySerial}</span>
      <span className="text-text-700">{label}</span>
    </label>
  );
};
